import React, { useMemo, useState } from 'react';
import { Box, Button, Link, Paper, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

import { CHATBOT_HE } from '../chatbotStrings.he';

const sanitizeHtml = (html) => {
  if (typeof html !== 'string' || !html.trim()) return '';
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
};

const parseMessageContent = (content) => {
  const elements = [];
  const youtubeMatches = [];

  const pattern1 =
    /(📹[^:\n]+:)\s*([^-\n]+?)\s*-\s*(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*)/gi;
  let match;
  while ((match = pattern1.exec(content)) !== null) {
    youtubeMatches.push({
      fullMatch: match[0],
      introText: match[1].trim(),
      label: match[2].trim(),
      url: match[3],
      videoId: match[4],
      index: match.index,
    });
  }

  const pattern2 = /📹\s*(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*)/gi;
  while ((match = pattern2.exec(content)) !== null) {
    const alreadyCaptured = youtubeMatches.some((m) => m.index === match.index);
    if (!alreadyCaptured) {
      youtubeMatches.push({
        fullMatch: match[0],
        introText: null,
        label: 'YouTube Video',
        url: match[1],
        videoId: match[2],
        index: match.index,
      });
    }
  }

  const pattern3 = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*)/gi;
  while ((match = pattern3.exec(content)) !== null) {
    const alreadyCaptured = youtubeMatches.some(
      (m) => m.url === match[1] || (m.index <= match.index && m.index + m.fullMatch.length > match.index)
    );
    if (!alreadyCaptured) {
      youtubeMatches.push({
        fullMatch: match[0],
        introText: null,
        label: 'YouTube Video',
        url: match[1],
        videoId: match[2],
        index: match.index,
      });
    }
  }

  youtubeMatches.sort((a, b) => a.index - b.index);

  if (youtubeMatches.length > 0) {
    let lastIndex = 0;

    youtubeMatches.forEach((ytMatch, idx) => {
      if (ytMatch.index > lastIndex) {
        const textBefore = content.substring(lastIndex, ytMatch.index);
        if (textBefore.trim()) {
          elements.push({ type: 'text', content: textBefore, key: `text-before-${idx}` });
        }
      }

      if (ytMatch.introText) {
        elements.push({ type: 'text', content: `${ytMatch.introText}\n`, key: `intro-${idx}` });
      }

      elements.push({
        type: 'youtube',
        videoId: ytMatch.videoId,
        label: ytMatch.label,
        url: ytMatch.url,
        key: `youtube-${idx}`,
      });

      lastIndex = ytMatch.index + ytMatch.fullMatch.length;
    });

    if (lastIndex < content.length) {
      const textAfter = content.substring(lastIndex);
      if (textAfter.trim()) elements.push({ type: 'text', content: textAfter, key: 'text-final' });
    }
  } else {
    elements.push({ type: 'text', content, key: 'text-only' });
  }

  return elements;
};

const renderInlineFormatting = (text) => {
  const elements = [];
  let key = 0;
  const combinedPattern = /(\*\*(.+?)\*\*)|(https?:\/\/[^\s]+)/g;
  let lastIndex = 0;
  let match;

  while ((match = combinedPattern.exec(text)) !== null) {
    if (match.index > lastIndex) elements.push(text.substring(lastIndex, match.index));

    if (match[1]) {
      elements.push(
        <Box component="strong" key={`bold-${key++}`} sx={{ fontWeight: 700 }}>
          {match[2]}
        </Box>
      );
    } else if (match[3]) {
      elements.push(
        <Link
          key={`link-${key++}`}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {match[3]}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) elements.push(text.substring(lastIndex));
  return elements.length > 0 ? elements : text;
};

const FormattedText = ({ content }) => {
  const lines = String(content || '').split('\n');

  return (
    <Box>
      {lines.map((line, idx) => {
        const listMatch = line.match(/^(\d+)\.\s+(.+)$/);
        if (listMatch) {
          return (
            <Box key={`line-${idx}`} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                {listMatch[1]}.
              </Typography>
              <Typography component="span">{renderInlineFormatting(listMatch[2])}</Typography>
            </Box>
          );
        }

        if (line.trim()) {
          return (
            <Typography key={`line-${idx}`} sx={{ mb: 1 }}>
              {renderInlineFormatting(line)}
            </Typography>
          );
        }

        return <Box key={`line-${idx}`} sx={{ height: 8 }} />;
      })}
    </Box>
  );
};

const YouTubeEmbed = ({ videoId, label, url }) => {
  const [showEmbed, setShowEmbed] = useState(false);

  return (
    <Paper variant="outlined" sx={{ my: 2, overflow: 'hidden', bgcolor: 'grey.50' }}>
      {!showEmbed ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                bgcolor: 'error.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon sx={{ color: '#fff' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <VideoLibraryIcon sx={{ fontSize: 20, color: 'error.main' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700 }} dir="rtl">
                  {CHATBOT_HE.videoTutorialTitle}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>{label}</Typography>

              <Button
                onClick={() => setShowEmbed(true)}
                variant="contained"
                color="error"
                size="small"
                startIcon={<PlayArrowIcon />}
                sx={{ textTransform: 'none' }}
              >
                Watch Video
              </Button>

              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ml: 2, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                Open in YouTube
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pb: '56.25%' }}>
          <Box
            component="iframe"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            src={`https://www.youtube.com/embed/${videoId}`}
            title={label}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      )}
    </Paper>
  );
};

export default function MessageContent({ content, htmlContent }) {
  const sanitizedHtml = useMemo(() => sanitizeHtml(htmlContent), [htmlContent]);
  const elements = useMemo(() => parseMessageContent(String(content || '')), [content]);

  if (sanitizedHtml) {
    return (
      <Box sx={{ fontSize: 14, lineHeight: 1.6 }} dir="auto">
        {content ? (
          <Box sx={{ mb: 1.5 }}>
            {elements.map((element) => {
              if (element.type === 'youtube') {
                return (
                  <YouTubeEmbed
                    key={element.key}
                    videoId={element.videoId}
                    label={element.label}
                    url={element.url}
                  />
                );
              }
              if (element.type === 'text') {
                return <FormattedText key={element.key} content={element.content} />;
              }
              return null;
            })}
          </Box>
        ) : null}

        <Box
          className="xtram-message-html"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            '& table': { width: 'auto', minWidth: '100%', borderCollapse: 'collapse', my: 1 },
            '& th, & td': { border: '1px solid #d1d5db', p: 1, verticalAlign: 'top', whiteSpace: 'nowrap' },
            '& p, & h1, & h2, & h3, & h4': { mb: 1 },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ fontSize: 14, lineHeight: 1.6 }} dir="auto">
      {elements.map((element) => {
        if (element.type === 'youtube') {
          return (
            <YouTubeEmbed
              key={element.key}
              videoId={element.videoId}
              label={element.label}
              url={element.url}
            />
          );
        }
        if (element.type === 'text') return <FormattedText key={element.key} content={element.content} />;
        return null;
      })}
    </Box>
  );
}

